<template>
  <div class="user-plan-cancel-page">
    <div class="flex justify-center text-center" v-if="isGettingMyPlan">
      <aha-spinner />
    </div>
    <div class="flex justify-center" v-if="!isGettingMyPlan">
      <div class="w-11/12 sm:w-2/3 lg:w-7/12">
        <!-- Reason cancel subscription -->
        <div
          class="card-body"
          v-if="isOfferReasonVisible"
        >
          <img
            src="@/assets/images/cancellation-offer/1-leave reason.jpg"
            class="card-body__image"
          >
          <div class="card-body__content">
            <h2 class="font-bold">
              We’re sorry to see you go.
            </h2>
            <h2 class="pb-3 font-bold">
              Could you share why?
            </h2>
            <div>
              <div class="text-area">
                <aha-textarea
                  class="form-control"
                  v-model="textReasonCancelSub"
                  @focus="typeTextarea"
                  @blur="outFocus"
                  :maxLength="800"
                  placeholder="You can always tell us the reason…"
                />
                <div class="count-text" v-if="isFocus">
                  {{ 800 - textReasonCancelSub.length }}
                </div>
              </div>
            </div>
            <div class="flex flex-col items-center pt-5">
              <aha-button
                @click="handleSubmitCancelSubscription()"
                :disabled="isButtonSubmitDisabled"
                class="w-full"
              >
                Continue with cancellation
                <aha-spinner
                  variant="white"
                  v-if="isCancellingSubscription"
                  class="ml-1"
                />
              </aha-button>
              <div class="cancel__button-go-back">
                <aha-link @click="handleClickOnIllStay()">
                  Go back
                </aha-link>
              </div>
            </div>
          </div>
        </div>

        <!-- Offer cancel subscription -->
        <div class="card-body" v-if="!cancelSuccess && isOfferContentVisible">
          <img
            src="@/assets/images/cancellation-offer/2-give a deal.jpg"
            class="card-body__image"
          >
          <div class="card-body__content">
            <h2 class="font-bold">
              Hey, {{ currentUser.firstName }} {{ currentUser.lastName }}!
            </h2>
            <h2 class="pb-3 font-bold">
              Would {{ offerPercentageText }} off help you out?
            </h2>
            <p>
              Your next payment will be charged at {{ offerPercentageText }}
              the usual price
            </p>
            <p>(that's <span class="font-bold">${{ offerAmount }} off</span>).</p>
            <p class="pt-3">
              If you continue with your cancellation, the deal will be gone!
            </p>
            <div class="flex flex-col items-center w-full pt-5">
              <aha-button
                variant="primary"
                @click="getTheOffer()"
                class="w-full"
                :disabled="isCancellingSubscription || isProcessingThinkLater"
              >
                Give me {{ offerPercentageText }} off!
                <aha-spinner
                  variant="white"
                  v-if="isGettingOffer"
                  class="ml-1"
                />
              </aha-button>
              <aha-button
                variant="base"
                class="w-full mt-5"
                @click="thinkLater"
                :disabled="isGettingOffer || isCancellingSubscription"
              >
                I will decide later
                <aha-spinner
                  variant="dark"
                  v-if="isProcessingThinkLater"
                  class="ml-1"
                />
              </aha-button>
              <div class="cancel__continue-link">
                <aha-link
                  @click="submitCancelSubscription()"
                  class="pt-6 text-sm font-bold"
                  :disabled="isGettingOffer || isProcessingThinkLater"
                >
                  I still want to cancel
                  <aha-spinner
                    v-if="isCancellingSubscription"
                    class="ml-1"
                    size="xs"
                  />
                </aha-link>
              </div>
            </div>
          </div>
        </div>

        <!-- Cancel subscription success -->
        <div class="card-body" v-if="cancelSuccess">
          <img
            src="@/assets/images/cancellation-offer/5-cancel.jpg"
            class="card-body__image"
          >
          <div class="card-body__content">
            <div v-html="cancelSuccessHeading"></div>
            <h2 class="pb-3 font-bold">
              Hopefully we’ll see you again!
            </h2>
            <p>
              Head back to the <aha-link @click="goBackToMyPlan">
                My Plan
              </aha-link> page to reactivate your subscription any time.
            </p>
            <p class="pt-3">
              Your {{ plan.name }} plan will remain active until <span
                v-if="endDate"
              >{{ endDate | dateTimeStamp }}</span>.
              <span v-if="!isWhiteLabelOrSkoletube">
                After that, you can use AhaSlides’ free features.</span>
            </p>
            <p v-if="!isRemoveCardPurpose" class="pt-3">
              You can always come back to access your previous presentations.
            </p>
            <div class="flex justify-end pt-5">
              <aha-button class="btn btn-primary" @click="goBackToMyPlan()">
                Got it!
              </aha-button>
            </div>
          </div>
        </div>

        <!-- Congrats on taking offer / Think later -->
        <div
          class="card-body"
          v-if="isOfferCongratsVisible ? isOfferCongratsVisible : isOfferThinkLaterVisible"
        >
          <img
            src="@/assets/images/cancellation-offer/4-congratulations.jpg"
            class="card-body__image"
          >
          <div class="card-body__content">
            <div v-html="offerCongratsHeading"></div>
            <div v-html="offerCongratsContent"></div>
            <div class="flex justify-end pt-5">
              <aha-button variant="primary" @click="goBackToMyPlan()">
                Got it!
              </aha-button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import Config from '@/config'
import Pricing from '@/pricing'
import Util from '@/util'
import StripeApi from '@/api/stripe'
import gaCategories from '@/constant/gaCategories'
import storageKeys from '@/constant/storageKeys'
import mixpanelUtils from '@/utils/mixpanel.utils'

const errorOnCancellation = 'Something went wrong, please check your connection or contact us for support'

export default {
  name: 'UserCancelSub',
  data() {
    return {
      data: {},
      url: '',
      cancelSuccess: false,
      isOfferContentVisible: false,
      isOfferCongratsVisible: false,
      isOfferThinkLaterVisible: false,
      textReasonCancelSub: '',
      isFocus: false,
      endDate: '',
      plan: {},
      errorOnCancellation,
      isCancellingSubscription: false,
      isProcessingThinkLater: false,
      isGettingMyPlan: true,
      isGettingOffer: false,
    }
  },

  computed: {
    ...mapState({
      currentUser: (state) => state.currentUser,
    }),

    ...mapGetters('autoOffer', ['cancellationOffer']),
    isOfferReasonVisible() {
      return !this.cancelSuccess && !this.isOfferContentVisible &&
      !this.isOfferCongratsVisible && !this.isOfferThinkLaterVisible
    },

    isButtonSubmitDisabled() {
      return this.textReasonCancelSub.replace(/\s/g, '') === ''
    },

    isRemoveCardPurpose() {
      // If not "remove card purpose", then is "cancel sub purpose" or undefined
      return localStorage.getItem(storageKeys.IS_REMOVE_CARD_PURPOSE) === 'true'
    },

    cancelSuccessHeading() {
      if (this.isRemoveCardPurpose) {
        return '<h2 class="font-bold">Card removed.</h2>'
      }
      return '<h2 class="font-bold">Subscription cancelled.</h2>'
    },

    alreadyClaimedPromotion() {
      return this.data.promotion
    },

    offerAmount() {
      const { subscription } = this.data
      if (!this.currentUser.id || !this.data || !subscription) return null
      if (this.currentUser.id % 2 === 0) {
        return (subscription.amount * 0.25).toFixed(2)
      }
      return (subscription.amount * 0.5).toFixed(2)
    },

    offerPercentageText() {
      if (!this.currentUser.id) return null
      if (this.currentUser.id % 2 === 0) return '25%'
      return '50%'
    },

    isWhiteLabelOrSkoletube() {
      const isWhiteLabel = Util.checkWhiteLabelDomain()
      const isSkoletube = this.currentUser.identifyProvider === 'skoletube'
      return isWhiteLabel || isSkoletube
    },

    notAllowedToSeeOffer() {
      return this.alreadyClaimedPromotion || this.isWhiteLabelOrSkoletube
    },

    offerCongratsHeading() {
      if (this.isOfferCongratsVisible) {
        return `
          <h2 class="pb-3">Yeaaahhh! Your participants will appreciate this decision!</h2>
        `
      }
      return `
        <h2 class="pb-3">Thanks for giving it another thought!</h2>
      `
    },

    offerCongratsContent() {
      if (this.isOfferCongratsVisible) {
        return `
          <p>We are so glad you decided to stay!</p>
          <p class="pt-3">You will get ${this.offerPercentageText} discount at the next payment. Please note that this offer also applies if you upgrade your plan.</p>
        `
      }
      return `
          <p>To make it a little easier for you, we have applied the offer to your account anyway. You’ll get ${this.offerPercentageText} off your next payment.</p>
          <p class="pt-3">You can still cancel your subscription whenever you want.</p>
        `
    },
  },
  watch: {
    '$store.state.currentUser': function watchCurrentUser() {
      this.getMyPlan()
    },
  },
  mounted() {
    document.body.classList.add('bg-base-0')
    this.getMyPlan()
  },

  destroyed() {
    document.body.classList.remove('bg-base-0')
  },

  methods: {
    ...mapActions(['setErrorOnInternalServerAction']),
    ...mapActions('autoOffer', ['getOfferOnCancellation']),
    getMyPlan() {
      if (!this.currentUser.id) return
      if (this.$store.state.currentUser.plan === 'free') {
        this.$router.push('/apps/user-plan')
        return
      }
      this.$http.get(Config.api.myPlan).then((res) => {
        if (!res.body.subscription) {
          this.goBackToMyPlan()
          return
        }
        this.data = res.body
        this.plan = Pricing.getPlanByType(res.body.subscription.type)
        this.isGettingMyPlan = false
      }).catch(() => {
        this.setErrorOnInternalServerAction('Something went wrong')
      })
    },

    typeTextarea() {
      this.isFocus = true
    },
    outFocus() {
      this.isFocus = false
    },

    _showOfferContent() {
      this.isOfferContentVisible = true
      Util.gaTrackEvent(
        gaCategories.MY_PLAN,
        'Auto-Offer on cancellation > I"ll cancel > Show offer',
        `Offer: ${this.offerPercentageText}`,
      )
    },

    handleSubmitCancelSubscription() {
      if (this.isButtonSubmitDisabled) return
      if (!this.notAllowedToSeeOffer) {
        this._showOfferContent()
        return
      }
      this.submitCancelSubscription()
    },

    submitCancelSubscription() {
      if (this.isCancellingSubscription) return
      Util.gaTrackEvent(
        gaCategories.MY_PLAN,
        'Auto-Offer on cancellation > I"ll cancel > Cancelling subscription',
        `Offer: ${this.offerPercentageText}`,
      )

      const reason = this.textReasonCancelSub.trim()
      if (!reason) return
      this.isCancellingSubscription = true
      if (this.isRemoveCardPurpose) {
        this._removeCard(reason)
        return
      }
      this._turnOffAutoRenew({
        stop: true,
        reason,
      })
    },

    async _removeCard(reason) {
      try {
        const { body: { ref } } = await StripeApi.removeCards()
        const payload = {
          stop: true,
          ref,
          reason,
        }
        await this._turnOffAutoRenew(payload)
      } catch {
        Util.showToastError(this.errorOnCancellation)
        this.isCancellingSubscription = false
      }
    },

    async _turnOffAutoRenew(payload) {
      try {
        const res = await StripeApi.cancelSubscription(payload)
        this.endDate = res.body.endDate
        const isRemoveCardPurpose = localStorage.getItem(storageKeys.IS_REMOVE_CARD_PURPOSE)
        if (isRemoveCardPurpose) {
          localStorage.removeItem(storageKeys.IS_REMOVE_CARD_PURPOSE)
        }
        this.isOfferContentVisible = false
        this.cancelSuccess = true
        Util.gaTrackEvent(
          gaCategories.MY_PLAN,
          'Cancel subscription',
          null,
          null,
          {
            offerContent: this.offerPercentageText,
            ...mixpanelUtils.getPlanContext(this.plan),
          },
        )
      } catch (error) {
        Util.showToastError(this.errorOnCancellation)
      } finally {
        this.isCancellingSubscription = false
      }
    },

    goBackToMyPlan() {
      this.$router.push({ name: 'UserPlan' })
    },

    getTheOffer() {
      if (this.isGettingOffer) return
      Util.gaTrackEvent(
        gaCategories.MY_PLAN,
        'Auto-Offer on cancellation > Take the Offer > Getting offer',
        `Offer: ${this.offerPercentageText}`,
      )
      this.isGettingOffer = true
      this.getOfferOnCancellation()
        .then(this.onGetOfferSuccess)
        .catch(() => {
          this.isGettingOffer = false
          Util.showToastError(this.errorOnCancellation)
        })
    },

    onGetOfferSuccess() {
      this.isGettingOffer = false
      this.isOfferContentVisible = false
      this.isOfferCongratsVisible = true
      Util.gaTrackEvent(
        gaCategories.MY_PLAN,
        'Auto-Offer on cancellation > Take the Offer > Successfully',
        `Offer: ${this.offerPercentageText}`,
      )
    },

    thinkLater() {
      if (this.isProcessingThinkLater) return
      this.isProcessingThinkLater = true
      this.getOfferOnCancellation()
        .then(this.onThinkLaterSuccess)
        .catch(() => {
          this.isProcessingThinkLater = false
          Util.showToastError(this.errorOnCancellation)
        })
    },

    onThinkLaterSuccess() {
      this.isGettingOffer = false
      this.isOfferContentVisible = false
      this.isOfferThinkLaterVisible = true
      this.isProcessingThinkLater = false
      Util.gaTrackEvent(
        gaCategories.MY_PLAN,
        'Auto-Offer on cancellation > Think later > Go back to my plan',
        `Offer: ${this.offerPercentageText}`,
      )
    },

    handleClickOnIllStay() {
      Util.gaTrackEvent(
        gaCategories.MY_PLAN,
        'Auto-Offer on cancellation > I"ll stay > Go back to My Plan',
      )

      this.goBackToMyPlan()
    },
  },
}
</script>

<style lang="scss" scoped>
@import "@/assets/scss/variable.scss";
.user-plan-cancel-page {
  max-width: 1024px;
  @apply mx-auto sm:my-12 mt-12 mb-20;
}
.content-title {
  margin: 32px 0;
  font-size: 30px;
}
.content-img img {
  max-width: 300px;

  @media (max-width: 480px) {
    max-width: 100%;
  }
}
.text-area {
  position: relative;
  .count-text {
    position: absolute;
    bottom: 15px;
    width: 3em;
    right: 0;
    color: #999;
  }
}
p.note-cancel-sub {
  font-size: 13px;
}

.card-body {
  .card-body__image {
    @apply my-0 mx-auto max-w-full;
  }
  .card-body__content {
    @apply w-11/12 sm:w-2/3 my-0 mx-auto pt-6;
  }
  .note {
    color: #666;
  }
  @media (max-width: 500px) {
    padding-left: 10px;
    padding-right: 10px;
  }
}

.cancel__continue-link {
  & ::v-deep .aha-link--base {
    @apply text-base-60 hover:text-base-70;
  }
}

.cancel__button-go-back {
  @apply pt-4 font-bold;
  & ::v-deep .aha-link--base {
    @apply text-base-70 hover:text-base-80;
  }
}
</style>
